import React, { useEffect } from 'react';
import { WarningCircleIcon } from '@phosphor-icons/react';
import { Modal } from '@tranquility-ai/theme-components/modal';
import '@tranquility-ai/theme-components/modal.css';
import { Button } from '@tranquility-ai/theme-components/button';

import { CONTENT_MODERATION_ACK_MODAL_Z_CLASS } from '../side-panel';

export type ContentModerationAckModalProps = {
  /** Detection type, e.g. "CSAM". Rendered emphasized in the body copy. */
  contentModerationType: string;
  onCancel: () => void;
  onAcknowledge: () => void;
};

/**
 * Shared "Content Warning" acknowledgement modal shown before previewing
 * content-moderation-flagged media. Standardized so it looks and behaves
 * identically wherever a flagged source/file is previewed.
 */
export function ContentModerationAckModal({
  contentModerationType,
  onCancel,
  onAcknowledge,
}: ContentModerationAckModalProps) {
  useEffect(() => {
    const markOverlay = () => {
      const overlay = document.querySelector<HTMLElement>(
        `.${CSS.escape(CONTENT_MODERATION_ACK_MODAL_Z_CLASS)}`,
      );
      overlay?.setAttribute('data-question-bar-interactive', '');
      return overlay;
    };

    // Theme Modal portals on a later effect, so the overlay is absent on this
    // effect's first run. Watch body children until it appears.
    markOverlay();
    const observer = new MutationObserver(() => {
      if (markOverlay()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onCancel]);

  return (
    <Modal
      isOpen
      title="Content Warning"
      aria-label="Content warning"
      size="sm"
      showCloseButton={false}
      dismissOnBackdropClick
      dismissOnEscape={false}
      onClose={onCancel}
      className={CONTENT_MODERATION_ACK_MODAL_Z_CLASS}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button
            type="button"
            size="sm"
            style="text"
            color="light"
            background="inverse"
            className="rounded-md px-4 py-2 text-sm text-white/70"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            style="solid"
            color="danger"
            background="inverse"
            className="rounded-md px-4 py-2 text-sm font-semibold"
            onClick={onAcknowledge}
          >
            Acknowledge &amp; Preview
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        <WarningCircleIcon size={24} weight="fill" className="text-destructive shrink-0" />
        <p className="text-sm text-white/70">
          This content has been flagged for:{' '}
          <span className="text-destructive font-semibold">{contentModerationType}</span>. The
          preview is obfuscated by default. Previewing the original will be logged and audited. You
          can toggle to the original after acknowledging.
        </p>
      </div>
    </Modal>
  );
}
